import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Edit2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import PageHeader from '../components/PageHeader';
import { toast } from 'sonner';
import { calculateTotal } from '../lib/orderUtils';

const ReviewProductSection = ({ order, onEdit }) => (
  <div className="flex items-start justify-between mb-6 pb-6 border-b border-border">
    <div className="flex items-start gap-4">
      <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
        <img src={order.product?.image} alt={order.product?.name} className="w-full h-full object-cover" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-foreground mb-1">{order.product?.name}</h3>
        <p className="text-sm text-muted-foreground mb-1">{order.preparation?.name}</p>
        <p className="text-sm text-muted-foreground">{order.flavor?.name}</p>
      </div>
    </div>
    <div className="text-right">
      <p className="text-xl font-bold text-foreground mb-2">{'\u20AC'}{order.product?.price.toFixed(2)}</p>
      <Button variant="ghost" size="sm" onClick={() => onEdit('product')} className="text-primary hover:text-primary/80">
        <Edit2 className="w-4 h-4 mr-1" /> Wijzig
      </Button>
    </div>
  </div>
);

const ReviewBurgerSection = ({ order, onEdit }) => {
  if (!order.burger) return null;
  return (
    <div className="flex items-start justify-between mb-6 pb-6 border-b border-border">
      <div>
        <h4 className="text-sm font-semibold text-muted-foreground mb-2">Burger optie</h4>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg font-semibold text-foreground">Met broodje</span>
        </div>
        {order.burgerSauce && order.burgerSauce.id !== 'no-sauce' && (
          <p className="text-sm text-muted-foreground">Saus: {order.burgerSauce.name}</p>
        )}
      </div>
      <div className="text-right">
        <p className="text-lg font-semibold text-foreground mb-1">+{'\u20AC'}1.50</p>
        {order.burgerSauce && order.burgerSauce.price > 0 && (
          <p className="text-sm text-foreground mb-2">+{'\u20AC'}{order.burgerSauce.price.toFixed(2)}</p>
        )}
        <Button variant="ghost" size="sm" onClick={() => onEdit('burger-option')} className="text-primary hover:text-primary/80">
          <Edit2 className="w-4 h-4 mr-1" /> Wijzig
        </Button>
      </div>
    </div>
  );
};

const ReviewIncludedSection = () => (
  <div className="mb-6 pb-6 border-b border-border">
    <h4 className="text-sm font-semibold text-muted-foreground mb-3">Inbegrepen</h4>
    <div className="flex items-center gap-3 bg-primary/5 p-4 rounded-lg">
      <Check className="w-5 h-5 text-primary" />
      <span className="text-lg font-semibold text-foreground">Signature Ribbled Chips</span>
      <span className="ml-auto text-lg font-bold text-primary">GRATIS</span>
    </div>
  </div>
);

const ReviewExtrasSection = ({ extras, onEdit }) => {
  if (!extras || extras.length === 0) return null;
  return (
    <div className="mb-6 pb-6 border-b border-border">
      <h4 className="text-sm font-semibold text-muted-foreground mb-3">Extra's</h4>
      <div className="space-y-2">
        {extras.map((extra) => (
          <div key={extra.id} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-foreground">{extra.name}</span>
              {extra.quantity > 1 && <span className="text-sm text-muted-foreground">x{extra.quantity}</span>}
            </div>
            <p className="text-lg font-semibold text-foreground">
              {'\u20AC'}{(extra.price * extra.quantity).toFixed(2)}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-3">
        <Button variant="ghost" size="sm" onClick={() => onEdit('side')} className="text-primary hover:text-primary/80">
          <Edit2 className="w-4 h-4 mr-1" /> Wijzig extras
        </Button>
      </div>
    </div>
  );
};

export const OrderReview = ({ order }) => {
  const navigate = useNavigate();
  const total = calculateTotal(order);

  const handleEdit = (step) => navigate(`/${step}`);

  const handleProceedToPayment = () => {
    if (!order.product || !order.preparation || !order.flavor) {
      toast.error('Vul alle vereiste velden in');
      return;
    }
    navigate('/payment');
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <PageHeader title="Controleer je bestelling" subtitle="Bekijk en bevestig je bestelling" />

      <div className="container max-w-4xl mx-auto px-6 mt-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Card className="p-8 bg-card border-2 border-border">
            <ReviewProductSection order={order} onEdit={handleEdit} />
            <ReviewBurgerSection order={order} onEdit={handleEdit} />
            <ReviewIncludedSection />
            <ReviewExtrasSection extras={order.extraSides} onEdit={handleEdit} />

            <Separator className="my-6" />

            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-foreground">Totaal</h3>
              <p className="text-3xl font-bold text-primary">{'\u20AC'}{total.toFixed(2)}</p>
            </div>

            <Button
              onClick={handleProceedToPayment}
              size="lg"
              data-testid="proceed-to-payment-btn"
              className="w-full btn-kiosk bg-primary hover:bg-primary/90 text-primary-foreground text-xl font-semibold shadow-lg"
            >
              Ga naar betalen
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderReview;
